// Business setup (IA §6.1 steps 2–3; ORG-001 + STORE-001): business name and store link, then the
// shell opens on Today. Progress is server state: a user with no membership sees the full form; an
// Owner whose business has no store yet resumes at the store link (any device). Later IA steps
// (first product, delivery, payments, publish) arrive with their modules as launch-list items.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  isValidStoreSlug,
  STAGING_HOST_SUFFIX,
  STORE_APEX_DOMAIN,
  storeHostSuffixFor,
  suggestStoreSlug,
} from "@ady/contracts";
import { t } from "@ady/i18n";
import { Banner, Button, Field, focusFirstInvalid, Input } from "@ady/ui";
import { ApiError, api } from "../api/client";
import type { CreatedOrg } from "../api/types";
import { errorMessage, isUnauthenticated } from "../api/errors";
import { useIdempotencyKeys } from "../api/idempotency";
import { keys, useMe } from "../api/queries";
import { AuthLayout } from "../auth/AuthLayout";
import { LegalGate } from "../auth/LegalGate";
import { LoginShowcase } from "../auth/LoginShowcase";
import { useGo } from "../shell/AppLink";
import { defaultStore } from "../shell/context";
import { TID } from "../testids";

const NAME_MAX = 120;

/** Four random base-36 characters for an auto-chosen link that turned out to be taken. */
function linkSuffix(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  return Array.from(bytes, (b) => (b % 36).toString(36)).join("");
}

type FieldErrors = { name?: string; slug?: string; referral?: string };

/** PARTNER-01: the same rule as the server (packages/domain/billing/src/referral.ts). */
export const REFERRAL_RE = /^[A-Z0-9-]{3,32}$/;
export const normalizeReferral = (v: string) => v.trim().toUpperCase();

function slugError(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  if (err.code === "validation.slug_taken") return t("onboarding.slugTaken");
  if (err.code === "validation.slug_reserved") return t("onboarding.slugReserved");
  if (err.code === "validation.invalid" && err.fields?.slug) return t("onboarding.slugFormat");
  return undefined;
}

export function OnboardingPage() {
  const me = useMe();
  const go = useGo();
  const qc = useQueryClient();
  const keyFor = useIdempotencyKeys();
  const formRef = useRef<HTMLFormElement>(null);
  const createdOrg = useRef<CreatedOrg | null>(null);

  const [name, setName] = useState("");
  const [slugInput, setSlugInput] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  // D-067: a referral link (/sign-in?ref=CODE) fills the code in (kept through sign-in).
  const [referral, setReferral] = useState(() => {
    try {
      return (
        new URLSearchParams(location.search).get("ref") ??
        sessionStorage.getItem("ady.referral") ??
        ""
      );
    } catch {
      return "";
    }
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const ready = me.data ? defaultStore(me.data) : null;
  useEffect(() => {
    if (me.isError && isUnauthenticated(me.error)) go("/sign-in?next=/onboarding");
    else if (ready) go(`/${ready.org}/${ready.store}/today`);
  }, [me.isError, me.error, ready, go]);

  // Resume: a business this user can set up (Owner/Manager) that has no store yet.
  const pending = me.data?.memberships.find(
    (m) => m.stores.length === 0 && m.permissions.includes("org.update"),
  );
  const businessName = pending ? pending.org.name : name;
  const slug = slugEdited ? slugInput : suggestStoreSlug(businessName);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    const orgName = businessName.trim().normalize("NFC");
    const next: FieldErrors = {};
    if (!orgName) next.name = t("onboarding.orgNameRequired");
    if (!slug) next.slug = t("onboarding.slugRequired");
    else if (!isValidStoreSlug(slug)) next.slug = t("onboarding.slugFormat");
    else if (slug.endsWith(STAGING_HOST_SUFFIX)) next.slug = t("onboarding.slugReserved");
    const referralCode = normalizeReferral(referral);
    if (!pending && referralCode && !REFERRAL_RE.test(referralCode))
      next.referral = t("wallet.referral.invalid");
    setErrors(next);
    if (next.name || next.slug || next.referral) {
      requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      return;
    }

    setBusy(true);
    try {
      let org: CreatedOrg | null = pending?.org ?? createdOrg.current;
      if (!org) {
        org = await api.createOrg(
          orgName,
          keyFor({ org: orgName, referral: referralCode }),
          referralCode || undefined,
        );
        createdOrg.current = org; // a store failure below resumes here, never creating a 2nd org
      }
      // An auto-suggested link that is taken gets a short suffix (up to 3 tries); a link the vendor
      // typed is never changed behind their back.
      let link = slug;
      for (let attempt = 0; ; attempt++) {
        const body = { name: orgName, slug: link };
        try {
          const store = await api.createStore(org.id, body, keyFor({ orgId: org.id, ...body }));
          await qc.invalidateQueries({ queryKey: keys.me });
          go(`/${org.slug}/${store.slug}/today`);
          return;
        } catch (err) {
          const taken = err instanceof ApiError && err.code === "validation.slug_taken";
          if (!taken || slugEdited || attempt >= 2) throw err;
          link = `${slug.slice(0, 58).replace(/-+$/, "")}-${linkSuffix()}`;
        }
      }
    } catch (err) {
      const fieldMsg = slugError(err);
      if (err instanceof ApiError && err.fields?.referral_code) {
        setErrors({ referral: t("wallet.referral.invalid") });
        requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      } else if (fieldMsg) {
        setErrors({ slug: fieldMsg });
        requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      } else if (err instanceof ApiError && err.code.startsWith("entitlement.")) {
        setFormError(t("onboarding.storeLimit"));
      } else {
        setFormError(errorMessage(err));
      }
      // The org may now exist: refresh so a reload resumes at the store step.
      if (createdOrg.current) void qc.invalidateQueries({ queryKey: keys.me });
    } finally {
      setBusy(false);
    }
  };

  if (!me.data || ready) return null;

  return (
    <LegalGate>
      <AuthLayout
        title={
          pending ? t("onboarding.resumeTitle", { name: pending.org.name }) : t("onboarding.title")
        }
        lead={pending ? t("onboarding.resumeLead") : t("onboarding.lead")}
        testId={TID.onboardingPage}
        showcase={<LoginShowcase />}
      >
        {formError ? <Banner kind="error" title={formError} data-testid={TID.formError} /> : null}
        <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-3">
          {pending ? null : (
            <Field label={t("onboarding.orgNameLabel")} error={errors.name} required>
              <Input
                autoComplete="organization"
                value={name}
                maxLength={NAME_MAX * 4}
                maxGraphemes={NAME_MAX}
                userContent
                onChange={(e) => setName(e.target.value)}
                data-testid={TID.onboardingOrgName}
              />
            </Field>
          )}
          <Field label={t("onboarding.slugLabel")} error={errors.slug} required>
            <Input
              value={slug}
              inputMode="url"
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              maxLength={63}
              suffix={`${storeHostSuffixFor(location.hostname)}.${STORE_APEX_DOMAIN}`}
              onChange={(e) => {
                setSlugEdited(true);
                setSlugInput(e.target.value.toLowerCase().replace(/\s+/g, "-"));
              }}
              data-testid={TID.onboardingStoreSlug}
            />
          </Field>
          {pending ? null : (
            <Field label={t("wallet.referral.label")} error={errors.referral}>
              <Input
                value={referral}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                maxLength={32}
                onChange={(e) => setReferral(e.target.value)}
                data-testid="onboarding-referral-code"
              />
            </Field>
          )}
          <Button
            type="submit"
            fullWidth
            loading={busy}
            loadingLabel={t("onboarding.submitting")}
            data-testid={TID.onboardingSubmit}
          >
            {pending ? t("onboarding.resumeSubmit") : t("onboarding.submit")}
          </Button>
        </form>
      </AuthLayout>
    </LegalGate>
  );
}
